import React, { useState, useEffect, useRef } from 'react';
import { useToast, useConfirm } from '../shared/AppDialogs';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

/**
 * CampaignsPage - Outbound Campaigns Management
 */
export function CampaignsPage({ agents }) {
  const toast = useToast();
  const confirm = useConfirm();
  const [campaigns, setCampaigns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [editingCampaign, setEditingCampaign] = useState(null);
  const [expandedCampaign, setExpandedCampaign] = useState(null);
  const [uploadingCsv, setUploadingCsv] = useState(null);
  const [schedulingCampaign, setSchedulingCampaign] = useState(null);
  const fileInputRef = useRef(null);

  // Load campaigns
  useEffect(() => {
    loadCampaigns();
  }, []);

  const loadCampaigns = async () => {
    try {
      const response = await fetch(`${API_URL}/api/campaigns`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('session_token')}`
        }
      });
      const data = await response.json();
      if (data.success) {
        setCampaigns(data.data || []);
      }
    } catch (err) {

    } finally {
      setLoading(false);
    }
  };

  const toggleCampaignStatus = async (campaignId, currentStatus) => {
    try {
      const newStatus = currentStatus === 'active' ? 'paused' : 'active';
      const response = await fetch(`${API_URL}/api/campaigns/${campaignId}/status`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('session_token')}`
        },
        body: JSON.stringify({ status: newStatus })
      });
      
      if (response.ok) {
        setCampaigns(prev => prev.map(c => 
          c.id === campaignId ? { ...c, status: newStatus } : c
        ));
      }
    } catch (err) {

    }
  };

  const deleteCampaign = async (campaignId) => {
    const ok = await confirm({ title: 'Kampagne löschen', message: 'Kampagne wirklich löschen?', confirmText: 'Löschen', type: 'danger' }); if (!ok) return;
    
    try {
      const response = await fetch(`${API_URL}/api/campaigns/${campaignId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('session_token')}`
        }
      });
      
      if (response.ok) {
        setCampaigns(prev => prev.filter(c => c.id !== campaignId));
      }
    } catch (err) {

    }
  };

  // Handle CSV upload
  const handleCsvUpload = async (campaignId, file) => {
    setUploadingCsv(campaignId);
    try {
      const formData = new FormData();
      formData.append('file', file);

      const response = await fetch(`${API_URL}/api/campaigns/${campaignId}/contacts/csv`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('session_token')}`
        },
        body: formData
      });

      const data = await response.json();
      if (data.success) {
        toast.success(`${data.count} Kontakte erfolgreich importiert`);
        loadCampaigns();
        setExpandedCampaign(campaignId);
      } else {
        toast.error(data.detail || 'Upload fehlgeschlagen');
      }
    } catch (err) {

      toast.error(err.message);
    } finally {
      setUploadingCsv(null);
    }
  };

  // Schedule campaign calls
  const scheduleCampaignCalls = async (campaignId) => {
    const ok2 = await confirm({ title: 'Anrufe planen', message: 'Anrufe für alle Kontakte planen?', confirmText: 'Planen', type: 'info' }); if (!ok2) return;
    
    setSchedulingCampaign(campaignId);
    try {
      const response = await fetch(`${API_URL}/api/campaigns/${campaignId}/schedule`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('session_token')}`
        }
      });

      const data = await response.json();
      if (data.success) {
        toast.success(`${data.scheduled_count} Anrufe wurden geplant`);
        loadCampaigns();
      } else {
        toast.error(data.detail || 'Planung fehlgeschlagen');
      }
    } catch (err) {

      toast.error(err.message);
    } finally {
      setSchedulingCampaign(null);
    }
  };

  const getAgentName = (agentId) => {
    const agent = agents?.find(a => a.id === agentId);
    return agent?.name || 'Unbekannt';
  };

  const getTriggerIcon = (triggerType) => {
    switch (triggerType) {
      case 'calendar': return '📅';
      case 'contact_list': return '📋';
      case 'webhook': return '🔗';
      case 'schedule': return '⏰';
      default: return '📤';
    }
  };

  const getTriggerLabel = (triggerType) => {
    switch (triggerType) {
      case 'calendar': return 'Kalender-Event';
      case 'contact_list': return 'Kontaktliste';
      case 'webhook': return 'Webhook';
      case 'schedule': return 'Zeitplan';
      default: return 'Manuell';
    }
  };

  if (loading) {
    return (
      <div className="p-6 flex items-center justify-center">
        <div className="animate-spin rounded-full h-10 w-10 border-4 border-emerald-600 border-t-transparent"></div>
      </div>
    );
  }

  return (
    <div className="p-6 w-full max-w-7xl mx-auto">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-800 mb-2">📤 Outbound-Kampagnen</h1>
          <p className="text-gray-600">Automatisierte ausgehende Anrufe verwalten</p>
        </div>
        <button
          onClick={() => { setEditingCampaign(null); setShowCreateModal(true); }}
          className="px-4 py-2 bg-gradient-to-r from-emerald-500 to-teal-600 text-white font-medium rounded-lg hover:from-emerald-600 hover:to-teal-700 transition-all flex items-center gap-2"
          data-testid="create-campaign-btn"
        >
          <span>+</span> Neue Kampagne
        </button>
      </div>

      {/* Campaign Templates */}
      {campaigns.length === 0 && (
        <div className="mb-8">
          <h2 className="text-lg font-semibold text-gray-700 mb-4">Schnellstart-Vorlagen</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {[
              { icon: '📅', name: 'Terminerinnerung', desc: '24h vor Termin anrufen', trigger: 'calendar' },
              { icon: '🔄', name: 'Follow-up', desc: 'Nach X Tagen nachfassen', trigger: 'schedule' },
              { icon: '📢', name: 'Kampagne', desc: 'Kontaktliste abtelefonieren', trigger: 'contact_list' },
            ].map((template, idx) => (
              <button
                key={idx}
                onClick={() => { 
                  setEditingCampaign({ 
                    name: template.name, 
                    trigger_type: template.trigger,
                    mission: '',
                    opening_message: ''
                  }); 
                  setShowCreateModal(true); 
                }}
                className="p-4 bg-white border-2 border-dashed border-gray-300 rounded-xl hover:border-emerald-400 hover:bg-emerald-50 transition-all text-left group"
              >
                <span className="text-3xl mb-2 block">{template.icon}</span>
                <h3 className="font-semibold text-gray-800 group-hover:text-emerald-700">{template.name}</h3>
                <p className="text-sm text-gray-500">{template.desc}</p>
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Campaigns List */}
      {campaigns.length > 0 ? (
        <div className="space-y-4">
          {campaigns.map((campaign) => (
            <div
              key={campaign.id}
              className="bg-white rounded-xl border border-gray-200 overflow-hidden hover:shadow-md transition-shadow"
            >
              <div className="p-5">
                <div className="flex items-start justify-between">
                  <div className="flex items-start gap-4">
                    {/* Icon */}
                    <div className={`w-12 h-12 rounded-xl flex items-center justify-center text-xl ${
                      campaign.status === 'active' 
                        ? 'bg-emerald-100 text-emerald-600' 
                        : 'bg-gray-100 text-gray-400'
                    }`}>
                      {getTriggerIcon(campaign.trigger_type)}
                    </div>
                    
                    {/* Info */}
                    <div>
                      <div className="flex items-center gap-3">
                        <h3 className="font-semibold text-gray-800">{campaign.name}</h3>
                        <span className={`text-xs px-2 py-0.5 rounded-full ${
                          campaign.status === 'active'
                            ? 'bg-emerald-100 text-emerald-700'
                            : 'bg-gray-100 text-gray-600'
                        }`}>
                          {campaign.status === 'active' ? '✅ Aktiv' : '⏸ Pausiert'}
                        </span>
                      </div>
                      <p className="text-sm text-gray-500 mt-1">{campaign.mission || 'Keine Mission definiert'}</p>
                      <div className="flex items-center gap-4 mt-2 text-xs text-gray-500">
                        <span>🤖 {getAgentName(campaign.agent_id)}</span>
                        <span>•</span>
                        <span>{getTriggerIcon(campaign.trigger_type)} {getTriggerLabel(campaign.trigger_type)}</span>
                        {campaign.contacts && (
                          <>
                            <span>•</span>
                            <span>👥 {campaign.contacts.length} Kontakte</span>
                          </>
                        )}
                        {campaign.stats && campaign.stats.total > 0 && (
                          <>
                            <span>•</span>
                            <span>📞 {campaign.stats.completed || 0}/{campaign.stats.total || 0} Anrufe</span>
                          </>
                        )}
                      </div>
                    </div>
                  </div>
                  
                  {/* Actions */}
                  <div className="flex items-center gap-2">
                    {/* Expand/Collapse button */}
                    <button
                      onClick={() => setExpandedCampaign(expandedCampaign === campaign.id ? null : campaign.id)}
                      className="p-2 text-gray-500 hover:bg-gray-100 rounded-lg"
                      title={expandedCampaign === campaign.id ? 'Einklappen' : 'Details'}
                    >
                      {expandedCampaign === campaign.id ? '🔼' : '🔽'}
                    </button>
                    <button
                      onClick={() => toggleCampaignStatus(campaign.id, campaign.status)}
                      className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
                        campaign.status === 'active'
                          ? 'bg-amber-100 text-amber-700 hover:bg-amber-200'
                          : 'bg-emerald-100 text-emerald-700 hover:bg-emerald-200'
                      }`}
                    >
                      {campaign.status === 'active' ? '⏸ Pausieren' : '▶️ Starten'}
                    </button>
                    <button
                      onClick={() => { setEditingCampaign(campaign); setShowCreateModal(true); }}
                      className="p-2 text-gray-500 hover:bg-gray-100 rounded-lg"
                      title="Bearbeiten"
                    >
                      ✏️
                    </button>
                    <button
                      onClick={() => deleteCampaign(campaign.id)}
                      className="p-2 text-red-500 hover:bg-red-50 rounded-lg"
                      title="Löschen"
                    >
                      🗑️
                    </button>
                  </div>
                </div>

                {/* Progress bar for campaigns with stats */}
                {campaign.stats && campaign.stats.total > 0 && (
                  <div className="mt-4 pt-4 border-t border-gray-100">
                    <div className="flex items-center justify-between text-xs text-gray-500 mb-1">
                      <span>Fortschritt</span>
                      <span>{Math.round((campaign.stats.completed / campaign.stats.total) * 100)}%</span>
                    </div>
                    <div className="h-2 bg-gray-100 rounded-full overflow-hidden">
                      <div 
                        className="h-full bg-gradient-to-r from-emerald-500 to-teal-500 rounded-full transition-all"
                        style={{ width: `${(campaign.stats.completed / campaign.stats.total) * 100}%` }}
                      />
                    </div>
                    <div className="flex items-center gap-4 mt-2 text-xs">
                      <span className="text-emerald-600">✅ {campaign.stats.success || 0} Erfolg</span>
                      <span className="text-amber-600">📵 {campaign.stats.not_reached || 0} Nicht erreicht</span>
                      <span className="text-red-600">❌ {campaign.stats.failed || 0} Abgelehnt</span>
                    </div>
                  </div>
                )}
              </div>

              {/* Expanded Section - Contacts & CSV Upload */}
              {expandedCampaign === campaign.id && (
                <div className="border-t border-gray-200 p-5 bg-gray-50">
                  {/* CSV Upload Section */}
                  {campaign.trigger_type === 'contact_list' && (
                    <div className="mb-4">
                      <h4 className="font-medium text-gray-700 mb-3">📋 Kontaktliste verwalten</h4>
                      <div className="flex items-center gap-3">
                        <input
                          type="file"
                          accept=".csv"
                          ref={fileInputRef}
                          className="hidden"
                          onChange={(e) => {
                            if (e.target.files?.[0]) {
                              handleCsvUpload(campaign.id, e.target.files[0]);
                              e.target.value = '';
                            }
                          }}
                        />
                        <button
                          onClick={() => fileInputRef.current?.click()}
                          disabled={uploadingCsv === campaign.id}
                          className="px-4 py-2 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 flex items-center gap-2"
                        >
                          {uploadingCsv === campaign.id ? (
                            <>
                              <span className="animate-spin">⏳</span>
                              Hochladen...
                            </>
                          ) : (
                            <>📄 CSV hochladen</>
                          )}
                        </button>
                        
                        {campaign.contacts && campaign.contacts.length > 0 && (
                          <button
                            onClick={() => scheduleCampaignCalls(campaign.id)}
                            disabled={schedulingCampaign === campaign.id || campaign.status !== 'active'}
                            className="px-4 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 disabled:opacity-50 flex items-center gap-2"
                          >
                            {schedulingCampaign === campaign.id ? (
                              <>
                                <span className="animate-spin">⏳</span>
                                Planen...
                              </>
                            ) : (
                              <>📅 Anrufe planen</>
                            )}
                          </button>
                        )}
                        
                        <span className="text-sm text-gray-500">
                          Format: CSV mit Spalten "phone/telefon", "name", "email"
                        </span>
                      </div>
                    </div>
                  )}

                  {/* Contacts List */}
                  {campaign.contacts && campaign.contacts.length > 0 ? (
                    <div>
                      <h4 className="font-medium text-gray-700 mb-3">
                        👥 Kontakte ({campaign.contacts.length})
                      </h4>
                      <div className="bg-white rounded-lg border border-gray-200 overflow-hidden max-h-64 overflow-y-auto">
                        <table className="w-full text-sm">
                          <thead className="bg-gray-50 sticky top-0">
                            <tr>
                              <th className="px-4 py-2 text-left text-gray-600 font-medium">Name</th>
                              <th className="px-4 py-2 text-left text-gray-600 font-medium">Telefon</th>
                              <th className="px-4 py-2 text-left text-gray-600 font-medium">Status</th>
                            </tr>
                          </thead>
                          <tbody className="divide-y divide-gray-100">
                            {campaign.contacts.slice(0, 20).map((contact) => (
                              <tr key={contact.id} className="hover:bg-gray-50">
                                <td className="px-4 py-2 text-gray-800">{contact.name || '-'}</td>
                                <td className="px-4 py-2 text-gray-600">{contact.phone_number}</td>
                                <td className="px-4 py-2">
                                  <span className={`text-xs px-2 py-0.5 rounded-full ${
                                    contact.status === 'completed' ? 'bg-green-100 text-green-700' :
                                    contact.status === 'failed' ? 'bg-red-100 text-red-700' :
                                    contact.status === 'scheduled' ? 'bg-blue-100 text-blue-700' :
                                    'bg-gray-100 text-gray-600'
                                  }`}>
                                    {contact.status === 'completed' ? '✅ Erledigt' :
                                     contact.status === 'failed' ? '❌ Fehlgeschlagen' :
                                     contact.status === 'scheduled' ? '⏳ Geplant' :
                                     '⏸ Ausstehend'}
                                  </span>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                        {campaign.contacts.length > 20 && (
                          <div className="px-4 py-2 bg-gray-50 text-sm text-gray-500 text-center">
                            ... und {campaign.contacts.length - 20} weitere Kontakte
                          </div>
                        )}
                      </div>
                    </div>
                  ) : (
                    <div className="text-center py-6 text-gray-500">
                      <div className="text-3xl mb-2">📭</div>
                      <p>Noch keine Kontakte. Laden Sie eine CSV-Datei hoch.</p>
                    </div>
                  )}
                </div>
              )}
            </div>
          ))}
        </div>
      ) : (
        <div className="text-center py-12 bg-gray-50 rounded-xl">
          <div className="text-5xl mb-4">📤</div>
          <h3 className="text-lg font-medium text-gray-800 mb-2">Noch keine Kampagnen</h3>
          <p className="text-gray-600 mb-4">Erstelle deine erste Outbound-Kampagne</p>
          <button
            onClick={() => setShowCreateModal(true)}
            className="px-4 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700"
          >
            + Erste Kampagne erstellen
          </button>
        </div>
      )}

      {/* Create/Edit Modal */}
      {showCreateModal && (
        <CampaignModal
          campaign={editingCampaign}
          agents={agents}
          onClose={() => { setShowCreateModal(false); setEditingCampaign(null); }}
          onSave={() => { loadCampaigns(); setShowCreateModal(false); setEditingCampaign(null); }}
        />
      )}
    </div>
  );
}

/**
 * CampaignModal - Create/Edit Campaign
 */
function CampaignModal({ campaign, agents, onClose, onSave }) {
  const [formData, setFormData] = useState({
    name: campaign?.name || '',
    agent_id: campaign?.agent_id || '',
    mission: campaign?.mission || '',
    opening_message: campaign?.opening_message || '',
    trigger_type: campaign?.trigger_type || 'calendar',
    trigger_config: campaign?.trigger_config || {
      hours_before: 24,
      days_after: 3,
      call_times: { start: '09:00', end: '18:00' },
      weekdays: [1, 2, 3, 4, 5],
      max_attempts: 3,
      retry_interval: 2,
      voicemail_action: 'leave_message'
    },
    outcomes: campaign?.outcomes || [
      { id: 'confirmed', label: 'Bestätigt', color: 'green' },
      { id: 'rescheduled', label: 'Verschoben', color: 'blue' },
      { id: 'cancelled', label: 'Abgesagt', color: 'red' },
      { id: 'not_interested', label: 'Kein Interesse', color: 'gray' },
      { id: 'callback', label: 'Rückruf gewünscht', color: 'amber' },
    ]
  });
  const [saving, setSaving] = useState(false);
  const [activeSection, setActiveSection] = useState(1);

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleConfigChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      trigger_config: { ...prev.trigger_config, [field]: value }
    }));
  };

  const handleSave = async () => {
    if (!formData.name || !formData.agent_id) {
      toast.warning('Bitte Name und Agent auswählen');
      return;
    }

    setSaving(true);
    try {
      const url = campaign?.id 
        ? `${API_URL}/api/campaigns/${campaign.id}`
        : `${API_URL}/api/campaigns`;
      
      const response = await fetch(url, {
        method: campaign?.id ? 'PUT' : 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('session_token')}`
        },
        body: JSON.stringify(formData)
      });

      if (response.ok) {
        onSave();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Unbekannter Fehler');
      }
    } catch (err) {
      toast.error(err.message);
    } finally {
      setSaving(false);
    }
  };

  const weekdays = [
    { id: 1, label: 'Mo' },
    { id: 2, label: 'Di' },
    { id: 3, label: 'Mi' },
    { id: 4, label: 'Do' },
    { id: 5, label: 'Fr' },
    { id: 6, label: 'Sa' },
    { id: 0, label: 'So' },
  ];

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-4xl max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="p-6 border-b border-gray-200 flex items-center justify-between">
          <h2 className="text-xl font-bold text-gray-800">
            {campaign?.id ? '✏️ Kampagne bearbeiten' : '📤 Neue Kampagne'}
          </h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 text-2xl">×</button>
        </div>

        {/* Progress Steps */}
        <div className="px-6 py-4 bg-gray-50 border-b border-gray-200">
          <div className="flex items-center justify-between">
            {[
              { num: 1, label: 'Grundlagen' },
              { num: 2, label: 'Mission' },
              { num: 3, label: 'Trigger' },
              { num: 4, label: 'Einstellungen' },
              { num: 5, label: 'Ergebnisse' },
            ].map((step, idx) => (
              <button
                key={step.num}
                onClick={() => setActiveSection(step.num)}
                className={`flex items-center gap-2 px-3 py-1 rounded-full text-sm font-medium transition-colors ${
                  activeSection === step.num
                    ? 'bg-emerald-100 text-emerald-700'
                    : 'text-gray-500 hover:text-gray-700'
                }`}
              >
                <span className={`w-6 h-6 rounded-full flex items-center justify-center text-xs ${
                  activeSection === step.num
                    ? 'bg-emerald-600 text-white'
                    : 'bg-gray-200 text-gray-600'
                }`}>{step.num}</span>
                <span className="hidden sm:inline">{step.label}</span>
              </button>
            ))}
          </div>
        </div>

        {/* Content */}
        <div className="p-6 overflow-y-auto flex-1">
          {/* Section 1: Basics */}
          {activeSection === 1 && (
            <div className="space-y-4">
              <h3 className="font-semibold text-gray-800 mb-4">1️⃣ Grundlagen</h3>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Kampagnen-Name *</label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => handleChange('name', e.target.value)}
                  placeholder="z.B. Terminerinnerung"
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Agent *</label>
                <select
                  value={formData.agent_id}
                  onChange={(e) => handleChange('agent_id', e.target.value)}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
                >
                  <option value="">Agent auswählen...</option>
                  {agents?.map(agent => (
                    <option key={agent.id} value={agent.id}>🤖 {agent.name}</option>
                  ))}
                </select>
                <p className="text-xs text-gray-500 mt-1">Der Agent, der die Anrufe durchführt</p>
              </div>
            </div>
          )}

          {/* Section 2: Mission */}
          {activeSection === 2 && (
            <div className="space-y-4">
              <h3 className="font-semibold text-gray-800 mb-4">2️⃣ Mission</h3>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">🎯 Was soll der Agent erreichen?</label>
                <textarea
                  value={formData.mission}
                  onChange={(e) => handleChange('mission', e.target.value)}
                  rows={3}
                  placeholder="z.B. Erinnere den Kunden an seinen Termin. Frage ob er bestätigen, verschieben oder absagen möchte."
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">📝 Gesprächseröffnung</label>
                <textarea
                  value={formData.opening_message}
                  onChange={(e) => handleChange('opening_message', e.target.value)}
                  rows={3}
                  placeholder="z.B. Hallo {{name}}, hier ist {{agent}} von {{firma}}. Ich rufe wegen Ihres Termins am {{datum}} um {{uhrzeit}} an."
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
                />
                <p className="text-xs text-gray-500 mt-1">
                  Verfügbare Variablen: {"{{name}}, {{agent}}, {{firma}}, {{datum}}, {{uhrzeit}}"}
                </p>
              </div>
            </div>
          )}

          {/* Section 3: Trigger */}
          {activeSection === 3 && (
            <div className="space-y-4">
              <h3 className="font-semibold text-gray-800 mb-4">3️⃣ Trigger - Wann wird angerufen?</h3>
              
              <div className="grid grid-cols-2 gap-3">
                {[
                  { id: 'calendar', icon: '📅', label: 'Kalender-Event', desc: 'X Stunden vor Termin' },
                  { id: 'contact_list', icon: '📋', label: 'Kontaktliste', desc: 'Liste abtelefonieren' },
                  { id: 'webhook', icon: '🔗', label: 'Webhook', desc: 'Bei externem Event' },
                  { id: 'schedule', icon: '⏰', label: 'Zeitplan', desc: 'Zu festen Zeiten' },
                ].map(trigger => (
                  <button
                    key={trigger.id}
                    onClick={() => handleChange('trigger_type', trigger.id)}
                    className={`p-4 rounded-xl border-2 text-left transition-all ${
                      formData.trigger_type === trigger.id
                        ? 'border-emerald-500 bg-emerald-50'
                        : 'border-gray-200 hover:border-gray-300'
                    }`}
                  >
                    <span className="text-2xl">{trigger.icon}</span>
                    <h4 className="font-medium text-gray-800 mt-2">{trigger.label}</h4>
                    <p className="text-xs text-gray-500">{trigger.desc}</p>
                  </button>
                ))}
              </div>

              {/* Trigger-specific config */}
              {formData.trigger_type === 'calendar' && (
                <div className="mt-4 p-4 bg-gray-50 rounded-lg">
                  <label className="block text-sm font-medium text-gray-700 mb-2">Stunden vor Termin anrufen</label>
                  <input
                    type="number"
                    value={formData.trigger_config.hours_before || 24}
                    onChange={(e) => handleConfigChange('hours_before', parseInt(e.target.value))}
                    className="w-24 px-3 py-2 border border-gray-300 rounded-lg"
                  />
                  <p className="text-xs text-gray-500 mt-2">Kalender-Integration erforderlich (unter Integrationen)</p>
                </div>
              )}

              {formData.trigger_type === 'contact_list' && (
                <div className="mt-4 p-4 bg-gray-50 rounded-lg">
                  <label className="block text-sm font-medium text-gray-700 mb-2">Kontaktliste</label>
                  <div className="flex gap-2">
                    <button className="px-4 py-2 bg-white border border-gray-300 rounded-lg hover:bg-gray-50">
                      📄 CSV hochladen
                    </button>
                    <button className="px-4 py-2 bg-white border border-gray-300 rounded-lg hover:bg-gray-50">
                      📋 Liste auswählen
                    </button>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Section 4: Settings */}
          {activeSection === 4 && (
            <div className="space-y-4">
              <h3 className="font-semibold text-gray-800 mb-4">4️⃣ Anruf-Einstellungen</h3>
              
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Anrufzeiten</label>
                  <div className="flex items-center gap-2">
                    <input
                      type="time"
                      value={formData.trigger_config.call_times?.start || '09:00'}
                      onChange={(e) => handleConfigChange('call_times', { ...formData.trigger_config.call_times, start: e.target.value })}
                      className="px-3 py-2 border border-gray-300 rounded-lg"
                    />
                    <span>bis</span>
                    <input
                      type="time"
                      value={formData.trigger_config.call_times?.end || '18:00'}
                      onChange={(e) => handleConfigChange('call_times', { ...formData.trigger_config.call_times, end: e.target.value })}
                      className="px-3 py-2 border border-gray-300 rounded-lg"
                    />
                  </div>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Wochentage</label>
                <div className="flex gap-2">
                  {weekdays.map(day => (
                    <button
                      key={day.id}
                      onClick={() => {
                        const current = formData.trigger_config.weekdays || [];
                        const newDays = current.includes(day.id)
                          ? current.filter(d => d !== day.id)
                          : [...current, day.id];
                        handleConfigChange('weekdays', newDays);
                      }}
                      className={`w-10 h-10 rounded-lg font-medium transition-colors ${
                        (formData.trigger_config.weekdays || []).includes(day.id)
                          ? 'bg-emerald-500 text-white'
                          : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                      }`}
                    >
                      {day.label}
                    </button>
                  ))}
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Max. Versuche</label>
                  <input
                    type="number"
                    value={formData.trigger_config.max_attempts || 3}
                    onChange={(e) => handleConfigChange('max_attempts', parseInt(e.target.value))}
                    min={1}
                    max={10}
                    className="w-24 px-3 py-2 border border-gray-300 rounded-lg"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Retry-Abstand (Stunden)</label>
                  <input
                    type="number"
                    value={formData.trigger_config.retry_interval || 2}
                    onChange={(e) => handleConfigChange('retry_interval', parseInt(e.target.value))}
                    min={1}
                    className="w-24 px-3 py-2 border border-gray-300 rounded-lg"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Bei Mailbox</label>
                <select
                  value={formData.trigger_config.voicemail_action || 'leave_message'}
                  onChange={(e) => handleConfigChange('voicemail_action', e.target.value)}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg"
                >
                  <option value="leave_message">Nachricht hinterlassen</option>
                  <option value="retry">Auflegen, später erneut versuchen</option>
                  <option value="skip">Auflegen, nicht erneut versuchen</option>
                </select>
              </div>
            </div>
          )}

          {/* Section 5: Outcomes */}
          {activeSection === 5 && (
            <div className="space-y-4">
              <h3 className="font-semibold text-gray-800 mb-4">5️⃣ Ergebnisse erfassen</h3>
              <p className="text-sm text-gray-600 mb-4">Definiere die möglichen Ausgänge eines Anrufs:</p>
              
              <div className="space-y-2">
                {formData.outcomes.map((outcome, idx) => (
                  <div key={outcome.id} className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg">
                    <span className={`w-3 h-3 rounded-full bg-${outcome.color}-500`}></span>
                    <input
                      type="text"
                      value={outcome.label}
                      onChange={(e) => {
                        const newOutcomes = [...formData.outcomes];
                        newOutcomes[idx].label = e.target.value;
                        handleChange('outcomes', newOutcomes);
                      }}
                      className="flex-1 px-3 py-2 border border-gray-300 rounded-lg"
                    />
                    <button
                      onClick={() => {
                        handleChange('outcomes', formData.outcomes.filter((_, i) => i !== idx));
                      }}
                      className="text-red-500 hover:bg-red-50 p-2 rounded-lg"
                    >
                      🗑️
                    </button>
                  </div>
                ))}
              </div>
              
              <button
                onClick={() => {
                  handleChange('outcomes', [
                    ...formData.outcomes,
                    { id: `custom_${Date.now()}`, label: 'Neues Ergebnis', color: 'gray' }
                  ]);
                }}
                className="text-emerald-600 hover:text-emerald-700 text-sm font-medium"
              >
                + Eigenes Ergebnis hinzufügen
              </button>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="p-6 border-t border-gray-200 flex items-center justify-between bg-gray-50">
          <button
            onClick={() => setActiveSection(Math.max(1, activeSection - 1))}
            disabled={activeSection === 1}
            className="px-4 py-2 text-gray-600 hover:text-gray-800 disabled:opacity-50"
          >
            ← Zurück
          </button>
          
          <div className="flex gap-3">
            <button
              onClick={onClose}
              className="px-4 py-2 text-gray-600 hover:bg-gray-200 rounded-lg"
            >
              Abbrechen
            </button>
            
            {activeSection < 5 ? (
              <button
                onClick={() => setActiveSection(activeSection + 1)}
                className="px-6 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700"
              >
                Weiter →
              </button>
            ) : (
              <button
                onClick={handleSave}
                disabled={saving}
                className="px-6 py-2 bg-gradient-to-r from-emerald-500 to-teal-600 text-white rounded-lg hover:from-emerald-600 hover:to-teal-700 disabled:opacity-50"
              >
                {saving ? 'Speichern...' : '💾 Kampagne speichern'}
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

export default CampaignsPage;
